/**
 * StrategyPanel — the strategy stage of the campaign canvas.
 *
 * Plan metrics, budget split bar, and per-platform messaging, all inline
 * editable. The approve button is the ONLY way to advance to creative.
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { EditableField } from './EditableField';
import { Segmented } from '@/components/growth-desk/ui';
import { PlatformLogo, platformBrand, type PlatformKey } from '@/components/icons/PlatformLogos';
import type { CampaignState, Gate } from './types';
import { getStrategyPlatforms } from './types';
import { AdvancedStrategyPanel } from '../chat/AdvancedStrategyPanel';

const PLATFORM_COLORS: Record<string, string> = {
  meta: platformBrand.meta.color,
  google: platformBrand.google_ads.color,
  google_ads: platformBrand.google_ads.color,
  tiktok: platformBrand.tiktok.color,
  pinterest: platformBrand.pinterest.color,
  twitter: platformBrand.twitter.color,
};

export interface StrategyPanelProps {
  state: CampaignState;
  readonly: boolean;
  onEdit: (path: string, value: unknown, label?: string) => Promise<unknown>;
  onApprove: (gate: Gate) => void;
  approving: boolean;
  isFlashing: (path: string) => boolean;
}

/** What the Campaign Compiler will publish for this canvas (geo, placements, bidding, objective +
 *  reasons), as the advanced settings panel reads it. Shared by the Strategy and Launch steps. */
export function advancedConfigFor(state: CampaignState): Record<string, unknown> {
  const budget = state.budget ?? {};
  const strategyPlatforms = getStrategyPlatforms(state);

  const cfg: Record<string, unknown> = {
    currency: budget.currency,
    budget_total: budget.total,
    // Nested budget so the compiler reads total/split/durationDays (see _assemble_brand_request).
    budget,
  };
  // Carry the LLM's strategy_intent VERBATIM (geo, interests, exclusions, bidding) so the
  // compiled preview reflects what the compiler ACTUALLY uses. Without it the compiler sees
  // no intent and falls back to the brand's service area (e.g. "Pune") — which is misleading
  // when the LLM actually targeted, say, the six metros. Putting `campaigns` at the top level
  // means build_compiled_preview skips canonical normalization and reads the intent directly.
  const si = (state as unknown as { strategy_intent?: { schema?: string; business_read?: unknown; campaigns?: unknown[] } }).strategy_intent;
  if (si && Array.isArray(si.campaigns) && si.campaigns.length) {
    cfg.schema = si.schema ?? 'campaign_strategy_intent';
    cfg.business_read = si.business_read;
    cfg.campaigns = si.campaigns;
    // The compiler reads a single top-level campaign_type (Meta Advantage+ / Google PMax); use Meta's.
    const metaCt = state.campaignType?.meta;
    if (metaCt && metaCt !== 'manual') cfg.campaign_type = metaCt;
  }
  // Per-platform keys drive the advanced view's platform TABS (AdvancedStrategyPanel detects
  // platforms from object-valued keys). Kept alongside `campaigns[]`, which the compiler uses.
  const plats = strategyPlatforms.size ? Array.from(strategyPlatforms) : ['meta'];
  for (const p of plats) {
    const key = p === 'facebook' || p === 'instagram' ? 'meta' : p;
    const ct = state.campaignType?.[key];
    cfg[key] = {
      campaign: { name: state.name ?? 'Campaign', objective: state.objective },
      ad_sets: [{ name: state.name ?? 'Campaign', ads: [] }],
      // Per-platform: so the compiled preview reflects that platform's Advantage+
      // guardrails (or the refusal), independently of the other platforms.
      ...(ct && ct !== 'manual' ? { campaign_type: ct } : {}),
    };
  }
  return cfg;
}

export const StrategyPanel: React.FC<StrategyPanelProps> = ({
  state,
  readonly,
  onEdit,
  onApprove,
  approving,
  isFlashing,
}) => {
  const [advanced, setAdvanced] = React.useState(false);
  const budget = state.budget ?? {};
  const split = budget.split ?? {};
  const splitEntries = Object.entries(split);
  const messaging = state.messaging ?? {};
  // Gemini emits the canonical platform id `google_ads`; older/other responses use
  // `google`. Read whichever key actually carries copy (matching the google||google_ads
  // tolerance the rest of the canvas already uses), and reuse that key for edit paths.
  const googleMsgKey = (messaging as any).google_ads ? 'google_ads' : 'google';
  const googleMsg = (messaging as any)[googleMsgKey] ?? {};
  const googleHeadlines = googleMsg.headlines ?? [];
  const googleDescriptions = googleMsg.descriptions ?? [];
  const currency = budget.currency === 'INR' ? '₹' : budget.currency === 'EUR' ? '€' : budget.currency === 'GBP' ? '£' : '$';

  const commit = (path: string, value: string | number) =>
    onEdit(path, value);

  // Editor for Meta DCO copy variations (headlines/bodies). Meta A/B-tests these
  // together with the primary headline/body on PLAIN images (dynamic creative);
  // finished ads (baked-in copy) ignore them. Add 2+ to activate DCO.
  const metaVariationList = (field: 'headlines' | 'bodies', label: string, multiline = false) => {
    const list = (messaging.meta?.[field] ?? []) as string[];
    return (
      <div className="border-t border-line px-4 py-3">
        <div className="mb-1.5 flex items-center justify-between">
          <span className="text-label uppercase text-ink-3">{label}</span>
          {!readonly && (
            <button
              type="button"
              className="text-small font-semibold text-brand-ink hover:underline"
              onClick={() => onEdit(`messaging.meta.${field}`, [...list, ''], `Added a ${label.toLowerCase()} option`)}
            >
              + Add
            </button>
          )}
        </div>
        {list.length === 0 ? (
          <p className="m-0 text-xs leading-snug text-ink-3">
            Add 2+ so Meta can auto-test the best combination (dynamic creative). Skipped for finished ads.
          </p>
        ) : (
          list.map((v, i) => (
            <div key={`${field}-${i}`} className="mb-1 flex items-center gap-1">
              <div className="flex-1">
                <EditableField
                  label={`#${i + 1}`}
                  value={v}
                  path={`messaging.meta.${field}[${i}]`}
                  onCommit={commit}
                  readonly={readonly}
                  multiline={multiline}
                />
              </div>
              {!readonly && (
                <button
                  type="button"
                  title="Remove"
                  className="px-1 text-ink-3 hover:text-bad"
                  onClick={() => {
                    const next = list.slice();
                    next.splice(i, 1);
                    onEdit(`messaging.meta.${field}`, next, `Removed a ${label.toLowerCase()} option`);
                  }}
                >
                  ✕
                </button>
              )}
            </div>
          ))
        )}
      </div>
    );
  };

  // A platform block is visible only if the strategy explicitly includes it.
  const strategyPlatforms = getStrategyPlatforms(state);

  // Config for the advanced preview: what the Campaign Compiler will actually publish
  // for this strategy (geo/placements/bidding/objective + reasons), from canvas state.
  const advancedPreviewConfig = React.useMemo(() => advancedConfigFor(state), [state]);
  const inStrategy = (p: string) =>
    strategyPlatforms.size === 0 || strategyPlatforms.has(p);

  return (
    <div className="space-y-3">
      {/* Control density — Simple (default) vs Advanced. Advanced surfaces the exact
          compiled parameters + the reason for each (placements, exclusions, bidding,
          geo granularity, guardrails) — Admeru's precision as an opt-in feature. */}
      <div className="flex items-center justify-between gap-3">
        <span className="text-small font-semibold text-ink-2">Strategy detail</span>
        <Segmented label="Strategy detail" value={advanced ? 'advanced' : 'simple'} onChange={id => setAdvanced(id === 'advanced')}
          items={[{ id: 'simple', label: 'Simple' }, { id: 'advanced', label: 'Advanced' }]} />
      </div>

      {/* Plan metrics */}
      <section className="rounded-panel bg-card p-6">
        <h3 className="mb-3 flex items-center justify-between font-display text-card-title font-extrabold text-ink">
          Plan
          {!readonly && <span className="font-sans text-xs font-normal text-ink-3">Click any value to edit</span>}
        </h3>
        <div className="grid grid-cols-2 gap-2.5 md:grid-cols-4">
          <div className={cn('rounded-[14px] bg-ground p-4', isFlashing('objective') && 'canvas-flash')}>
            <EditableField
              label="Objective"
              value={state.objective}
              path="objective"
              onCommit={commit}
              readonly={readonly}
            />
          </div>
          <div className={cn('rounded-[14px] bg-ground p-4', isFlashing('budget.total') && 'canvas-flash')}>
            <EditableField
              label="Total budget"
              value={budget.total !== undefined ? `${currency}${Number(budget.total).toLocaleString()}` : undefined}
              path="budget.total"
              onCommit={(p, v) => commit(p, typeof v === 'string' ? Number(String(v).replace(/[^0-9.]/g, '')) : v)}
              numeric
              readonly={readonly}
            />
          </div>
          <div className={cn('rounded-[14px] bg-ground p-4', isFlashing('audience.summary') && 'canvas-flash')}>
            <EditableField
              label="Audience"
              value={state.audience?.summary}
              path="audience.summary"
              onCommit={commit}
              readonly={readonly}
            />
          </div>
          <div className={cn('rounded-[14px] bg-ground p-4', isFlashing('budget.durationDays') && 'canvas-flash')}>
            <EditableField
              label="Duration (days)"
              value={budget.durationDays}
              path="budget.durationDays"
              onCommit={commit}
              numeric
              readonly={readonly}
            />
          </div>
        </div>
      </section>

      {/* Delivery — PER PLATFORM. Precise is the recommended default (Admeru's whole
          value); Advantage+ / PMax hand delivery to the platform's AI. We don't promote
          it — we're honest that we add guardrails but can't guarantee precision. */}
      {(() => {
        let deliveryPlatforms = [
          inStrategy('meta') ? 'meta' : null,
          (inStrategy('google') || inStrategy('google_ads')) ? 'google' : null,
        ].filter(Boolean) as string[];
        // Never let the control vanish (regression guard): if the split keys don't
        // clearly map to meta/google, fall back to offering both — Advantage+ (Meta)
        // and Performance Max (Google) are the only AI-delivery options anyway.
        if (deliveryPlatforms.length === 0) deliveryPlatforms = ['meta', 'google'];
        // Tolerate a legacy string campaignType (pre per-platform) so it still shows.
        const rawCt = state.campaignType as unknown;
        const ctMap: Record<string, string> =
          typeof rawCt === 'string' ? { meta: rawCt, google: rawCt } : ((rawCt as Record<string, string>) ?? {});
        const advName = (p: string) => (p === 'google' ? 'Performance Max' : 'Advantage+');
        const label = (p: string) => (p === 'google' ? 'Google' : 'Meta');
        return (
          <section className="rounded-panel bg-card p-6">
            <h3 className="mb-4 font-display text-card-title font-extrabold text-ink">Delivery</h3>
            <div className={cn('gap-x-6 gap-y-4', deliveryPlatforms.length > 1 ? 'grid sm:grid-cols-2' : 'flex')}>
              {deliveryPlatforms.map((p) => {
                const mode = ctMap[p] === 'advantage_plus' ? 'advantage_plus' : 'manual';
                const opts: Array<['manual' | 'advantage_plus', string, string]> = [
                  ['manual', 'Precise', 'recommended — you control targeting, placements & spend'],
                  ['advantage_plus', advName(p), `${label(p)}'s AI controls delivery`],
                ];
                const isAdvantage = mode === 'advantage_plus';
                const setMode = (val: 'manual' | 'advantage_plus') =>
                  onEdit(`campaignType.${p}`, val, `${label(p)} delivery: ${val === 'advantage_plus' ? opts[1][1] : opts[0][1]}`);
                return (
                  <div key={p} className="flex flex-col gap-1.5">
                    <span className="text-small font-semibold text-ink-2">{label(p)}</span>
                    {readonly
                      ? <span className="text-[15px] font-semibold text-ink">{isAdvantage ? opts[1][1] : opts[0][1]}</span>
                      : <Segmented label={`${label(p)} delivery`} value={mode} onChange={id => setMode(id as 'manual' | 'advantage_plus')}
                          items={[{ id: 'manual', label: `${opts[0][1]} · recommended` }, { id: 'advantage_plus', label: opts[1][1] }]} />}
                  </div>
                );
              })}
            </div>
            <p className="m-0 mt-4 text-small leading-normal text-ink-2">
              We recommend <strong>Precise</strong> — Admeru controls targeting, placements and spend so
              budget isn&rsquo;t wasted. <strong>Advantage+ / Performance Max</strong> hand delivery to the
              platform&rsquo;s AI for broad reach; Admeru layers on guardrails (existing-customer exclusion
              <em> when a customer list is connected on Meta</em>, budget floor, brand-safe placements,
              brand-term negatives) and needs a pixel. We sharpen it, but delivery is ultimately the
              platform&rsquo;s — <strong>we can&rsquo;t guarantee precision</strong>.
            </p>
          </section>
        );
      })()}

      {/* Budget split */}
      {splitEntries.length > 0 && (
        <section
          className={cn(
            'rounded-panel bg-card p-6',
            isFlashing('budget.split') && 'canvas-flash',
          )}
        >
          <h3 className="mb-3 font-display text-card-title font-extrabold text-ink">Budget split</h3>
          <div className="flex h-2.5 overflow-hidden rounded-full">
            {splitEntries.map(([platform, pct]) => (
              <span
                key={platform}
                style={{
                  width: `${pct}%`,
                  backgroundColor: PLATFORM_COLORS[platform] ?? '#8884d8',
                  transition: 'width 0.5s ease',
                }}
              />
            ))}
          </div>
          <div className="mt-2.5 flex flex-wrap justify-between gap-2 text-small text-ink-2">
            {splitEntries.map(([platform, pct]) => (
              <span key={platform}>
                {(platformBrand as any)[platform === 'google' ? 'google_ads' : platform]?.name ?? platform} {pct}%
                {budget.total !== undefined &&
                  ` — ${currency}${Math.round((Number(budget.total) * Number(pct)) / 100).toLocaleString()}`}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Messaging — one card per platform */}
      {(() => {
        const platforms = [
          {
            key: 'meta',
            logoKey: 'meta' as PlatformKey,
            label: platformBrand.meta.name,
            show: inStrategy('meta') && !!(messaging.meta || !readonly),
            render: () => (
              <>
                <div className={cn('border-b border-line px-4 py-3', isFlashing('messaging.meta.headline') && 'canvas-flash')}>
                  <EditableField label="Headline" value={messaging.meta?.headline} path="messaging.meta.headline" onCommit={commit} readonly={readonly} />
                </div>
                <div className={cn('px-4 py-3', isFlashing('messaging.meta.body') && 'canvas-flash')}>
                  <EditableField label="Body" value={messaging.meta?.body} path="messaging.meta.body" onCommit={commit} readonly={readonly} multiline />
                </div>
                {metaVariationList('headlines', 'Headline variations')}
                {metaVariationList('bodies', 'Body variations', true)}
              </>
            ),
          },
          {
            key: 'google',
            logoKey: 'google_ads' as PlatformKey,
            label: 'Google Search',
            show: (inStrategy('google') || inStrategy('google_ads')) && !!(googleHeadlines.length || googleDescriptions.length || !readonly),
            render: () => (
              <>
                {googleHeadlines.map((h, i) => (
                  <div key={`gh-${i}`} className={cn('border-b border-line px-4 py-3', isFlashing(`messaging.${googleMsgKey}.headlines.${i}`) && 'canvas-flash')}>
                    <EditableField label={`Headline ${i + 1}`} value={h} path={`messaging.${googleMsgKey}.headlines[${i}]`} onCommit={commit} readonly={readonly} />
                  </div>
                ))}
                {googleDescriptions.map((d, i) => (
                  <div key={`gd-${i}`} className={cn(i < googleDescriptions.length - 1 ? 'border-b border-line' : '', 'px-4 py-3', isFlashing(`messaging.${googleMsgKey}.descriptions.${i}`) && 'canvas-flash')}>
                    <EditableField label={`Description ${i + 1}`} value={d} path={`messaging.${googleMsgKey}.descriptions[${i}]`} onCommit={commit} readonly={readonly} multiline />
                  </div>
                ))}
              </>
            ),
          },
          {
            key: 'pinterest',
            logoKey: 'pinterest' as PlatformKey,
            label: platformBrand.pinterest.name,
            show: inStrategy('pinterest') && !!(messaging.pinterest?.title || messaging.pinterest?.description || !readonly),
            render: () => (
              <>
                <div className={cn('border-b border-line px-4 py-3', isFlashing('messaging.pinterest.title') && 'canvas-flash')}>
                  <EditableField label="Title" value={messaging.pinterest?.title} path="messaging.pinterest.title" onCommit={commit} readonly={readonly} />
                </div>
                <div className={cn('px-4 py-3', isFlashing('messaging.pinterest.description') && 'canvas-flash')}>
                  <EditableField label="Description" value={messaging.pinterest?.description} path="messaging.pinterest.description" onCommit={commit} readonly={readonly} multiline />
                </div>
              </>
            ),
          },
          {
            key: 'tiktok',
            logoKey: 'tiktok' as PlatformKey,
            label: platformBrand.tiktok.name,
            show: inStrategy('tiktok') && !!((messaging as any).tiktok?.script || (messaging as any).tiktok?.text || !readonly),
            render: () => (
              <div className="px-4 py-3">
                <EditableField label="Script / Caption" value={messaging.tiktok?.script ?? messaging.tiktok?.text} path="messaging.tiktok.script" onCommit={commit} readonly={readonly} multiline />
              </div>
            ),
          },
          {
            key: 'twitter',
            logoKey: 'twitter' as PlatformKey,
            label: platformBrand.twitter.name,
            show: inStrategy('twitter') && !!((messaging as any).twitter?.tweet || (messaging as any).twitter?.text || !readonly),
            render: () => (
              <div className="px-4 py-3">
                <EditableField label="Tweet" value={messaging.twitter?.tweet ?? messaging.twitter?.text} path="messaging.twitter.tweet" onCommit={commit} readonly={readonly} multiline />
              </div>
            ),
          },
        ].filter((p) => p.show);

        if (platforms.length === 0) return null;

        return (
          <section className={cn('rounded-panel bg-card p-6', isFlashing('messaging') && 'canvas-flash')}>
            <h3 className="mb-3 flex items-center justify-between font-display text-card-title font-extrabold text-ink">
              Messaging <span className="font-sans text-xs font-normal text-ink-3">Per platform</span>
            </h3>
            <div className="space-y-3">
              {platforms.map((p) => (
                <div key={p.key} className="overflow-hidden rounded-[14px] shadow-ring-line">
                  {/* Platform header */}
                  <div className="flex items-center gap-2.5 border-b border-line bg-ground px-4 py-2.5">
                    <PlatformLogo platform={p.logoKey} size="xs" />
                    <span className="font-display text-sm font-bold text-ink">{p.label}</span>
                  </div>
                  {/* Fields */}
                  {p.render()}
                </div>
              ))}
            </div>
          </section>
        );
      })()}

      {/* Advanced strategy — the exact compiled parameters + the reason behind each.
          Gated by the density toggle: novices aren't overwhelmed; experts get full
          control + the decision trace on demand. */}
      {advanced ? (
        <>
          {(typeof (state.campaignType as unknown) === 'string'
            ? (state.campaignType as unknown) === 'advantage_plus'
            : Object.values(state.campaignType ?? {}).includes('advantage_plus')) && (
            <div className="rounded-btn bg-warn-soft p-3.5 text-small leading-normal text-ink">
              <strong>Advantage+ / Performance Max is on for a platform.</strong> There, targeting &amp;
              placements are managed by the platform&rsquo;s AI — the precise levers below don&rsquo;t all
              apply. What Admeru enforces shows in the decision trace: existing-customer exclusion, budget
              floor, brand-safe intent, and brand-term negatives (Google).
            </div>
          )}
          <AdvancedStrategyPanel campaignConfig={advancedPreviewConfig} />
        </>
      ) : (
        <button
          type="button"
          onClick={() => setAdvanced(true)}
          className="min-h-[44px] w-full rounded-btn text-sm font-semibold text-ink-2 shadow-ring-line hover:text-ink hover:shadow-ring-ink transition-shadow"
        >
          Show advanced — exactly what Admeru will publish (placements, exclusions,
          bidding, geo) and why →
        </button>
      )}

      {/* Decide bar */}
      {!readonly && (
        <section aria-label="Decide" className="sticky bottom-4 z-10 mt-2 flex flex-wrap items-center justify-between gap-3.5 rounded-panel bg-navy py-3.5 pl-[22px] pr-3.5 text-on-navy shadow-decide">
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="font-display text-base font-bold">Strategy ready for your OK</span>
            <span className="text-small text-on-navy-2">Nothing is made or spent until you approve.</span>
          </div>
          <button type="button" onClick={() => onApprove('strategy')} disabled={approving}
            className="inline-flex min-h-[52px] items-center rounded-btn bg-brand px-[22px] font-display text-base font-bold text-brand-on hover:bg-brand-hover disabled:cursor-wait disabled:opacity-60">
            {approving ? 'Approving…' : 'Approve strategy →'}
          </button>
        </section>
      )}
    </div>
  );
};
